import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Select,
  InputNumber,
  Row,
  Input,
  Pagination,
  Collapse,
  Tag,
  message,
  Grid,
} from "antd";
import {
  assignPackageB2B,
  assignTestB2B,
  fetchAdminPackages,
  get_all_tests,
  getAllB2BClients,
  getAllClientServices,
} from "../../endpoints/API";
import BulkUploadB2BTest from "./BulkUploadB2BTest";

const { Option } = Select;
const { Panel } = Collapse;
const { useBreakpoint } = Grid;

const B2BClientServicePage = () => {
  const [isTestModalVisible, setIsTestModalVisible] = useState(false);
  const [isPackageModalVisible, setIsPackageModalVisible] = useState(false);
  const [clients, setClients] = useState([]);
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [data, setData] = useState([]);

  const [testForm] = Form.useForm();
  const [packageForm] = Form.useForm();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);

  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async (pageNumber = 1, searchText = "") => {
    setLoading(true);
    try {
      const [clientServicesRes, clientsRes] = await Promise.all([
        getAllClientServices({ page: pageNumber, search: searchText }),
        getAllB2BClients(),
      ]);
      setData(clientServicesRes?.data?.results || []);
      setTotal(clientServicesRes?.data?.count || 0);
      setClients(clientsRes.data);
    } catch (error) {
      message.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTest = async (values) => {
    try {
      await assignTestB2B(values);
      message.success("Test assigned successfully.");
      setIsTestModalVisible(false);
      testForm.resetFields();
      fetchDropdownData(page, search);
    } catch (err) {
      message.error(
        err.response?.data?.non_field_errors?.[0] || "Error assigning test."
      );
    }
  };

  const handleAssignPackage = async (values) => {
    try {
      await assignPackageB2B(values);
      message.success("Package assigned successfully.");
      setIsPackageModalVisible(false);
      packageForm.resetFields();
      fetchDropdownData(page, search);
    } catch (err) {
      message.error(
        err.response?.data?.non_field_errors?.[0] || "Error assigning package."
      );
    }
  };

  // Shared pill style for price tags
  const PricePill = ({ price }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 500,
        background: "#E6F1FB",
        color: "#0C447C",
        border: "0.5px solid #B5D4F4",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      ₹{price}
    </span>
  );

  return (
    <div style={{ padding: isMobile ? "12px 8px" : "24px", minHeight: "100vh" }}>

      {/* ── Top bar: search + action buttons ── */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 10 : 12,
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Input.Search
          placeholder="Search client name, email..."
          onSearch={(value) => {
            setPage(1);
            setSearch(value);
            fetchDropdownData(1, value);
          }}
          onChange={(e) => {
            if (e.target.value === "") {
              setSearch("");
              fetchDropdownData(1, "");
            }
          }}
          style={{ width: isMobile ? "100%" : 300 }}
          allowClear
        />

        {/* Action buttons — wrap on mobile */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            justifyContent: isMobile ? "flex-start" : "flex-end",
          }}
        >
          <Button
            type="primary"
            style={{ backgroundColor: "#007bff", borderColor: "#007bff" }}
            onClick={() => setIsBulkUploadModalOpen(!isBulkUploadModalOpen)}
          >
            Bulk Upload
          </Button>
          <Button
            type="primary"
            style={{ backgroundColor: "#007bff", borderColor: "#007bff" }}
            onClick={() => setIsTestModalVisible(true)}
          >
            Assign Test
          </Button>
          <Button
            type="primary"
            style={{ backgroundColor: "#007bff", borderColor: "#17a2b8" }}
            onClick={() => setIsPackageModalVisible(true)}
          >
            Assign Package
          </Button>
        </div>
      </div>

      {/* ── Page title ── */}
      <h2
        style={{
          color: "#003366",
          fontSize: isMobile ? 16 : 20,
          marginBottom: 16,
          marginTop: 0,
        }}
      >
        B2B Clients & Services
      </h2>

      {/* ── Client service cards ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
          Loading...
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
          No clients found.
        </div>
      ) : (
        data.map((clientData) => (
          <div
            key={clientData.client.id}
            style={{
              marginBottom: 16,
              background: "var(--color-background-primary, #fff)",
              border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.12))",
              borderLeft: "3px solid #007bff",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            {/* Card header */}
            <div
              style={{
                padding: isMobile ? "12px 14px" : "14px 20px",
                background: "var(--color-background-secondary, #f9f9f7)",
                borderBottom: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
              }}
            >
              {/* Client name + email row */}
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "center",
                  justifyContent: "space-between",
                  gap: 6,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: isMobile ? 14 : 15,
                      fontWeight: 500,
                      color: "#003366",
                      marginBottom: 4,
                    }}
                  >
                    {clientData.client.name}
                  </div>

                  {/* Contact details — 2-col grid on tablet+, stacked on mobile */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "auto auto",
                      gap: isMobile ? "3px 0" : "2px 24px",
                    }}
                  >
                    <div style={{ fontSize: 12, color: "var(--color-text-secondary, #666)" }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 500,
                          color: "var(--color-text-tertiary, #999)",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginRight: 5,
                        }}
                      >
                        Email
                      </span>
                      {clientData.client.contact_email}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--color-text-secondary, #666)" }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 500,
                          color: "var(--color-text-tertiary, #999)",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginRight: 5,
                        }}
                      >
                        POC
                      </span>
                      {clientData.client.point_of_contact_name}
                      {clientData.client.point_of_contact_email && (
                        <span style={{ color: "#aaa", marginLeft: 4 }}>
                          · {clientData.client.point_of_contact_email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Count badges */}
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexShrink: 0,
                    marginTop: isMobile ? 8 : 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      padding: "3px 10px",
                      borderRadius: 20,
                      background: "#E1F5EE",
                      color: "#085041",
                      border: "0.5px solid #9FE1CB",
                      fontWeight: 500,
                    }}
                  >
                    {clientData.tests?.length || 0} Tests
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      padding: "3px 10px",
                      borderRadius: 20,
                      background: "#EEEDFE",
                      color: "#3C3489",
                      border: "0.5px solid #CECBF6",
                      fontWeight: 500,
                    }}
                  >
                    {clientData.packages?.length || 0} Packages
                  </span>
                </div>
              </div>
            </div>

            {/* Collapse panels */}
            <Collapse
              ghost
              expandIconPosition="end"
              style={{ padding: isMobile ? "0 4px" : "0 8px" }}
            >
              {/* Tests panel */}
              <Panel
                header={
                  <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary, #111)" }}>
                    Assigned Tests
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        padding: "1px 8px",
                        borderRadius: 20,
                        background: "#E1F5EE",
                        color: "#085041",
                        border: "0.5px solid #9FE1CB",
                      }}
                    >
                      {clientData.tests?.length || 0}
                    </span>
                  </span>
                }
                key="1"
              >
                {clientData.tests?.length ? (
                  <div style={{ paddingBottom: 4 }}>
                    {clientData.tests.map((t, idx) => (
                      <div
                        key={t.test_name}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 4px",
                          borderBottom:
                            idx < clientData.tests.length - 1
                              ? "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.07))"
                              : "none",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            color: "var(--color-text-primary, #111)",
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: isMobile ? "normal" : "nowrap",
                          }}
                        >
                          {t.test_name}
                        </span>
                        <PricePill price={t.b2b_price} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: "var(--color-text-tertiary, #aaa)", padding: "6px 4px" }}>
                    No tests assigned
                  </div>
                )}
              </Panel>

              {/* Packages panel */}
              <Panel
                header={
                  <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary, #111)" }}>
                    Assigned Packages
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        padding: "1px 8px",
                        borderRadius: 20,
                        background: "#EEEDFE",
                        color: "#3C3489",
                        border: "0.5px solid #CECBF6",
                      }}
                    >
                      {clientData.packages?.length || 0}
                    </span>
                  </span>
                }
                key="2"
              >
                {clientData.packages?.length ? (
                  <div style={{ paddingBottom: 4 }}>
                    {clientData.packages.map((p, idx) => (
                      <div
                        key={p.package_name}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 4px",
                          borderBottom:
                            idx < clientData.packages.length - 1
                              ? "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.07))"
                              : "none",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            color: "var(--color-text-primary, #111)",
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: isMobile ? "normal" : "nowrap",
                          }}
                        >
                          {p.package_name}
                        </span>
                        <PricePill price={p.b2b_price} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: "var(--color-text-tertiary, #aaa)", padding: "6px 4px" }}>
                    No packages assigned
                  </div>
                )}
              </Panel>
            </Collapse>
          </div>
        ))
      )}

      {/* ── Pagination ── */}
      <div
        style={{
          display: "flex",
          justifyContent: isMobile ? "center" : "flex-end",
          marginTop: 16,
        }}
      >
        <Pagination
          current={page}
          total={total}
          pageSize={10}
          onChange={(newPage) => {
            setPage(newPage);
            fetchDropdownData(newPage, search);
          }}
          showSizeChanger={false}
          simple={isMobile}
        />
      </div>

      {/* ── Assign Test Modal ── */}
      <Modal
        title="Assign Test to B2B Client"
        open={isTestModalVisible}
        onCancel={() => setIsTestModalVisible(false)}
        onOk={() => testForm.submit()}
        width={isMobile ? "95vw" : 520}
        centered={isMobile}
      >
        <Form form={testForm} layout="vertical" onFinish={handleAssignTest}>
          <Form.Item
            name="b2b_client"
            label="B2B Client"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select B2B Client">
              {clients.map((client) => (
                <Option key={client.id} value={client.id}>
                  {client.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="test_name"
            label="Test"
            rules={[{ required: true, message: "Please enter a test name" }]}
          >
            <Input placeholder="Enter Test Name" />
          </Form.Item>
          <Form.Item
            name="b2b_price"
            label="B2B Price"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Assign Package Modal ── */}
      <Modal
        title="Assign Package to B2B Client"
        open={isPackageModalVisible}
        onCancel={() => setIsPackageModalVisible(false)}
        onOk={() => packageForm.submit()}
        width={isMobile ? "95vw" : 520}
        centered={isMobile}
      >
        <Form form={packageForm} layout="vertical" onFinish={handleAssignPackage}>
          <Form.Item
            name="b2b_client"
            label="B2B Client"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select B2B Client">
              {clients.map((client) => (
                <Option key={client.id} value={client.id}>
                  {client.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="package_name"
            label="Package"
            rules={[{ required: true, message: "Please enter a package name" }]}
          >
            <Input placeholder="Enter Package Name" />
          </Form.Item>
          <Form.Item
            name="b2b_price"
            label="B2B Price"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Bulk Upload ── */}
      <BulkUploadB2BTest
        B2BClients={clients}
        isBulkUploadModalOpen={isBulkUploadModalOpen}
        setIsBulkUploadModalOpen={setIsBulkUploadModalOpen}
        onSuccess={() => fetchDropdownData(page, search)}
      />
    </div>
  );
};

export default B2BClientServicePage;